import { create } from 'zustand';
import { PendingCarton, RTVWMSCarton, RTVWMSCartonProduct, RTVWMSPallet } from '../types';
import {
  buildPallet as apiBuildPallet,
  getPalletCases as apiGetPalletCases,
  scanPallet as apiScanPallet,
} from 'services/api/palletBuild';

interface PalletBuildState {
  caseId: string | null;
  isCasePending: boolean;
  pallet_id: string | null;
  pallet: RTVWMSPallet | null;
  caseProducts: RTVWMSCartonProduct[];
  assignedCases: RTVWMSCarton[];
  isPalletPending: boolean;
  pendingCartons: PendingCarton[];
  setCaseId: (id: string | null, pending?: boolean) => void;
  setCaseProducts: (products: RTVWMSCartonProduct[]) => void;
  setPalletId: (id: string | null) => Promise<boolean>;
  buildPendingPallet: () => Promise<void>;
  reset: () => void;
}

export const usePalletBuildStore = create<PalletBuildState>((set, get) => ({
  caseId: null,
  isCasePending: false,
  pallet_id: null,
  pallet: null,
  caseProducts: [],
  assignedCases: [],
  isPalletPending: false,
  pendingCartons: [],

  setCaseId: (id, pending = false) => set({ caseId: id, caseProducts: [], isCasePending: pending }),
  setCaseProducts: (products) => set({ caseProducts: products }),

  setPalletId: async (id) => {
    if (!id) {
      set({
        pallet_id: null,
        pallet: null,
        assignedCases: [],
        isPalletPending: false,
        pendingCartons: [],
      });
      return false;
    }
    const scanResult = await apiScanPallet(id);
    const pallet = scanResult.success && scanResult.data.length > 0 ? scanResult.data[0] : null;
    const result = await apiGetPalletCases(id);
    if (!result.success) {
      set({
        pallet_id: id,
        pallet,
        assignedCases: [],
        isPalletPending: false,
        pendingCartons: [],
      });
      return false;
    }
    if (!result.data.palletExist) {
      set({
        pallet_id: id,
        pallet,
        assignedCases: [],
        isPalletPending: true,
        pendingCartons: [],
      });
      return false;
    }
    set({
      pallet_id: id,
      pallet,
      assignedCases: result.data.result,
      isPalletPending: false,
      pendingCartons: [],
    });
    return true;
  },

  buildPendingPallet: async () => {
    const { pallet_id, pendingCartons, assignedCases, caseId, isCasePending } = get();
    if (!pallet_id || pendingCartons.length === 0) return;
    const result = await apiBuildPallet({
      pallet_id,
      cartons: pendingCartons.map((c) => ({
        carton_id: c.carton_id,
        products: c.products.map((p) => ({ upc: p.product_id, product_qty: p.product_qty })),
      })),
    });
    if (!result.success) {
      throw new Error(result.message || 'Failed to build pallet');
    }
    const builtCartons = pendingCartons.map((c) => ({
      carton_id: c.carton_id,
      product_cnt: c.products.length,
    }));
    set({
      isPalletPending: false,
      isCasePending:
        isCasePending && pendingCartons.some((c) => c.carton_id === caseId) ? false : isCasePending,
      assignedCases: [...assignedCases, ...builtCartons],
      pendingCartons: [],
    });
  },

  reset: () =>
    set({
      caseId: null,
      isCasePending: false,
      pallet_id: null,
      pallet: null,
      caseProducts: [],
      assignedCases: [],
      isPalletPending: false,
      pendingCartons: [],
    }),
}));
